import { boundedPromise } from "@-/dev.improve.haniverse.lib.deadline";
import {
  SYSTEM_SENTENCE_VOICE_ID,
  type SentenceVoiceId,
} from "@-/dev.improve.haniverse.lib.sentence.speech.voice-catalog";

/** What the voice chooser UI receives when a sentence needs a voice the learner has not chosen. */
export type SentenceVoiceChoiceRequest = {
  /** Call while a chosen voice downloads, so the waiting sentence is not abandoned as stalled. */
  reportProgress: () => void;
  /** The voice to speak with, or `null` when the learner dismissed the prompt. */
  resolve: (voiceId: SentenceVoiceId | null) => void;
};

export type SentenceVoiceChooser = (request: SentenceVoiceChoiceRequest) => void;

/** The mounted chooser (one app-wide sheet), if any. */
const chooser: { current: SentenceVoiceChooser | null } = { current: null };

/** Called by the app-wide chooser sheet on mount; returns the unregister function. */
export function registerSentenceVoiceChooser(show: SentenceVoiceChooser): () => void {
  chooser.current = show;
  return () => {
    if (chooser.current === show) {
      chooser.current = null;
    }
  };
}

/**
 * Ask the learner which voice reads sentences. Resolves with their choice once it can speak (a
 * chosen download has finished), or `null` if they dismissed the prompt. Without a mounted chooser
 * (tests, or a surface outside the app shell) the system voice is used for this sentence only.
 *
 * Bounded like every wait: two minutes with no answer and no download progress gives up quietly.
 */
export function requestSentenceVoiceChoice(): Promise<SentenceVoiceId | null> {
  const show = chooser.current;
  if (show === null) {
    return Promise.resolve(SYSTEM_SENTENCE_VOICE_ID);
  }
  return boundedPromise<SentenceVoiceId | null>(
    { deadline: 120_000, operation: "sentence voice choice" },
    (resolve, _reject, _signal, reportProgress) => {
      show({ reportProgress, resolve });
    },
  ).catch(() => null);
}
